import {
  SimpleShowLayout,
  useRecordContext,
  Labeled,
  FunctionField,
  ReferenceField,
  useNotify,
  useRefresh,
  useResourceContext,
} from 'react-admin';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { CopiableField } from '@/components/CopiableIdField';
import { GenericRelativeDateDisplay } from '@/components/GenericDateFunctionField';
import ReactJson from 'react-json-view';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useCallback, useEffect, useState } from 'react';
import { getAppConfig } from '@/config/AppConfig';
import { getToken } from '@/providers/backendClient';

// Separate component for the Replay button and dialog
const ReplayOperationButton = ({ record }: { record: any }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const notify = useNotify();

  const refresh = useRefresh();

  const replayMutation = useCallback(async () => {
    if (!record) {
      return;
    }

    const appConfig = getAppConfig();
    const dlqReplayPath = `${appConfig.adminBackendUri}/dlq/replay`;
    const token = await getToken();

    try {
      setIsLoading(true);

      const result = await fetch(dlqReplayPath, {
        method: 'POST',
        body: JSON.stringify({
          dlq: record?.type,
          opId: record?.id,
        }),
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const resultJson = await result.json();

      if (!result.ok) {
        throw new Error(JSON.stringify(resultJson));
      }

      notify('Operation replayed successfully', { type: 'success' });
      refresh();
      setDialogOpen(false);

      setIsLoading(false);

      return resultJson;
    } catch (error: any) {
      console.error('Error replaying operation:', error);
      notify(`Failed to replay operation: ${error.message}`, {
        type: 'error',
      });

      setIsLoading(false);
    }
  }, [notify, record, refresh]);

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<RefreshIcon />}
        onClick={() => setDialogOpen(true)}
      >
        Replay
      </Button>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Replay Operation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to replay this Operation?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={replayMutation}
            color="primary"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Replaying...' : 'Replay'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const DeadLetterQueueCommandPanel = () => {
  const record = useRecordContext();
  const resource = useResourceContext();

  useEffect(() => {
    console.log(resource, record);
  }, [resource, record]);

  return (
    <SimpleShowLayout>
      <Panel />
    </SimpleShowLayout>
  );
};

const Panel = () => {
  const record = useRecordContext();

  useEffect(() => {
    console.log('RECORD', record);
  }, [record]);

  return (
    <>
      <Box display="flex" alignItems="center">
        <h2>Operation Details</h2>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2}>
        <Labeled label="Operation ID">
          <CopiableField content={record?._id} />
        </Labeled>
        <Labeled label="Timestamp">
          <GenericRelativeDateDisplay date={record?.when} />
        </Labeled>
        {record?.duration && (
          <Labeled label="Duration">
            <Typography>{record.duration}ms</Typography>
          </Labeled>
        )}
        <Labeled label="Collection">
          <Typography>{record?.crudOperation?.type}</Typography>
        </Labeled>
        <Labeled label="Document Id">
          <CopiableField content={record?.crudOperation?.id} />
        </Labeled>
        {record?.userMetadata?.id && (
          <Labeled label="User Id">
            <CopiableField content={record?.userMetadata?.id} />
          </Labeled>
        )}
        {record?.userMetadata?.type === 'therapist' && (
          <ReferenceField
            source="userMetadata.id"
            reference="therapists"
            link="show"
          >
            <FunctionField
              render={(record: any) => (
                <Box display="flex" flexDirection="row" gap="1rem">
                  <Labeled label="Therapist">
                    <Typography variant="subtitle2">
                      {`${record.title ? `${record.title} ` : ''}${
                        record.firstName
                      } ${record.lastName}`}
                    </Typography>
                  </Labeled>
                  <Labeled label="Email">
                    <Typography variant="subtitle2">
                      {record.email}
                    </Typography>
                  </Labeled>
                </Box>
              )}
            />
          </ReferenceField>
        )}
        {record?.lifecycle?.resolution?.type !== 'resolved' && (
          <Box>
            <ReplayOperationButton record={record} />
          </Box>
        )}
      </Box>

      <h2>Raw Data</h2>
      <Box sx={{ backgroundColor: '#2d2d2d', p: 2, borderRadius: 1 }}>
        <ReactJson
          src={record || {}}
          theme="monokai"
          displayDataTypes={false}
          enableClipboard={true}
          collapsed={2}
          style={{
            backgroundColor: 'transparent',
            fontFamily: 'monospace',
          }}
        />
      </Box>
    </>
  );
};
